/**
 * Notification WebSocket Service
 *
 * Manages Socket.io connection for real-time notifications:
 * - HTTP-only cookie authentication (no token parameter)
 * - Auto-reconnection
 * - Event handling for new notifications
 * - Unread count updates
 *
 * Events:
 * Server -> Client: notification:new, notification:unread-count
 * Client -> Server: notification:mark-read
 */

import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '../api/config';

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
}

export interface NewNotificationPayload {
  id: string;
  type: string;
  title: string;
  body: string;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface UnreadCountPayload {
  count: number;
}

type EventCallback<T> = (data: T) => void;

interface EventHandlers {
  connect: EventCallback<void>;
  disconnect: EventCallback<void>;
  'notification:new': EventCallback<NewNotificationPayload>;
  'notification:unread-count': EventCallback<UnreadCountPayload>;
  error: EventCallback<{ message: string; code?: string }>;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

export class NotificationSocketService {
  private static instance: NotificationSocketService;
  private socket: Socket | null = null;
  private connectionStatus: ConnectionStatus = 'disconnected';
  private eventHandlers: Map<string, Set<EventCallback<unknown>>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;

  private constructor() {}

  static getInstance(): NotificationSocketService {
    if (!NotificationSocketService.instance) {
      NotificationSocketService.instance = new NotificationSocketService();
    }
    return NotificationSocketService.instance;
  }

  /**
   * Connect to notifications WebSocket server
   * Fetches token from HTTP-only cookie and passes it via auth parameter
   * Handles authentication failures gracefully
   */
  async connect(): Promise<void> {
    if (this.socket?.connected) {
      return;
    }

    const socketUrl = API_CONFIG.baseUrl;

    try {
      // Import fetchWithTokenRefresh dynamically to avoid circular dependencies
      const { fetchWithTokenRefresh } = await import('../api/refreshToken');

      // Fetch WebSocket token from server (reads from HTTP-only cookie)
      // Uses fetchWithTokenRefresh to automatically retry with fresh token on 401
      const response = await fetchWithTokenRefresh(`${socketUrl}/api/v1/auth/ws-token`, {
        credentials: 'include', // Include HTTP-only cookies
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('[NotificationSocket] Not authenticated, cannot connect to WebSocket');
        } else {
          console.error('[NotificationSocket] Failed to get WS token:', response.status);
        }
        return;
      }

      const { token } = await response.json();

      this.socket = io(`${socketUrl}/notifications`, {
        auth: {
          token, // Pass token via auth parameter (supported by gateway)
        },
        withCredentials: true, // Still include cookies for additional security
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxReconnectAttempts,
      });

      this.setupEventListeners();
    } catch (error) {
      console.error('[NotificationSocket] Failed to connect:', error);
      this.updateConnectionStatus('disconnected');
    }
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.updateConnectionStatus('disconnected');
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.updateConnectionStatus('connected');
      this.emit('connect', undefined);
    });

    this.socket.on('disconnect', (reason) => {
      this.updateConnectionStatus('disconnected');
      this.emit('disconnect', undefined);

      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });

    // Reconnection events
    this.socket.on('reconnect_attempt', (attempt) => {
      this.reconnectAttempts = attempt;
      this.updateConnectionStatus('connecting');
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('[NotificationSocket] Reconnection error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[NotificationSocket] Reconnection failed after max attempts');
      this.updateConnectionStatus('disconnected');
    });

    // Notification events
    this.socket.on('notification:new', (data: NewNotificationPayload) => {
      this.emit('notification:new', data);
    });

    this.socket.on('notification:unread-count', (data: UnreadCountPayload) => {
      this.emit('notification:unread-count', data);
    });

    // Errors
    this.socket.on('error', (data: { message: string; code?: string }) => {
      console.error('[NotificationSocket] Server error:', data);
      this.emit('error', data);
    });
  }

  /**
   * Update connection status
   */
  private updateConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatus = status;
  }

  /**
   * Emit event to registered handlers
   */
  private emit<K extends keyof EventHandlers>(event: K, data: Parameters<EventHandlers[K]>[0]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      console.log(`[NotificationSocket] Emitting ${event} to ${handlers.size} handlers`);
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[NotificationSocket] Handler error for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Register event handler
   */
  on<K extends keyof EventHandlers>(event: K, handler: EventHandlers[K]): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler as EventCallback<unknown>);

    const currentSize = this.eventHandlers.get(event)!.size;
    console.log(`[NotificationSocket] Registered handler for ${event}, total handlers: ${currentSize}`);

    // Return unsubscribe function
    return () => {
      this.eventHandlers.get(event)?.delete(handler as EventCallback<unknown>);
      const remainingSize = this.eventHandlers.get(event)?.size || 0;
      console.log(`[NotificationSocket] Unregistered handler for ${event}, remaining handlers: ${remainingSize}`);
    };
  }

  /**
   * Unregister all handlers for an event
   */
  off<K extends keyof EventHandlers>(event: K): void {
    this.eventHandlers.delete(event);
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    if (!this.socket?.connected) return;

    this.socket.emit('notification:mark-read', { notificationId });
  }
}

// Export singleton instance
export const notificationSocketService = NotificationSocketService.getInstance();
